import type { DateValidationError } from '@mui/x-date-pickers';
import type { ReactNode } from 'react';
import type { FieldError } from 'react-hook-form';

import { CalendarToday } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';

import { DateFormat } from '@/shared/libs/time.lib';

import { fieldHelperTextSx } from '../../components/field-helper.text';
import { getLeaseDateAsteriskSx, leaseDatePickerStyles } from './lease-date.input.styles';
import { useBufferedDatePickerValue } from './use-buffered-date-picker-value';
import { type FormHelperTextProps } from '@mui/material';

const DEFAULT_MIN_DATE_OFFSET_DAYS = 14;

export type LeaseDatePickerProps = {
  title: string;
  value: dayjs.Dayjs | null;
  onChange: (newValue: dayjs.Dayjs | null) => void;
  onError: (error: DateValidationError, value: dayjs.Dayjs | null) => void;
  onBlur?: () => void;
  error?: FieldError;
  required?: boolean;
  disabled?: boolean;
  dataTestId: string;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  size?: 'small' | 'medium';
  redAsterisk?: boolean;
  asteriskColor?: string;
  helperText?: ReactNode;
};

export function LeaseDatePicker({
  title,
  value,
  onChange,
  onError,
  onBlur,
  error,
  required = false,
  disabled = false,
  dataTestId,
  minDate,
  maxDate,
  size = 'medium',
  redAsterisk = false,
  asteriskColor,
  helperText,
}: LeaseDatePickerProps) {
  const { pickerValue, handleChange, handleFocus, handleBlur } = useBufferedDatePickerValue({
    value,
    onChange,
    onBlur,
  });

  const asteriskSx = getLeaseDateAsteriskSx({ asteriskColor, redAsterisk });

  return (
    <DesktopDatePicker
      label={title}
      value={pickerValue}
      openTo="day"
      views={['year', 'day']}
      format={DateFormat.Us}
      disabled={disabled}
      minDate={minDate ?? dayjs().subtract(DEFAULT_MIN_DATE_OFFSET_DAYS, 'day')}
      maxDate={maxDate}
      onChange={handleChange}
      onError={onError}
      slots={{
        openPickerIcon: CalendarToday,
      }}
      slotProps={{
        actionBar: {
          sx: leaseDatePickerStyles.actionBar,
        },
        textField: {
          id: dataTestId,
          variant: 'filled',
          size,
          required,
          helperText: error?.message?.toString() ?? helperText ?? null,
          error: Boolean(error),
          'aria-label': `${title} - text field`,
          onFocus: handleFocus,
          onBlur: handleBlur,
          FormHelperTextProps: {
            'data-testid': `${dataTestId}-helper-text`,
            sx: fieldHelperTextSx,
          } as FormHelperTextProps & { 'data-testid': string },
        },
        openPickerButton: {
          'aria-label': `${title} - open calendar`,
          sx: leaseDatePickerStyles.openPickerButton,
        },
        inputAdornment: {
          sx: leaseDatePickerStyles.inputAdornment,
        },
        field: {
          onFocus: handleFocus,
        },
        day: { sx: leaseDatePickerStyles.day },
        yearButton: { sx: leaseDatePickerStyles.yearButton },
        monthButton: { sx: leaseDatePickerStyles.monthButton },
      }}
      sx={
        asteriskSx
          ? [leaseDatePickerStyles.datePicker, asteriskSx]
          : leaseDatePickerStyles.datePicker
      }
    />
  );
}
